import { Form, Row, Col, message } from "antd";
import { useEffect, useState } from "react";
import CustomInput from "../../../components/CustomInput/Input";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import FormFooter from "../../../components/CustomForms/FormFooter";
import CountryCode from "../../../components/CountryCodeSelect/CountryCode";
import {
  BTN_TXT,
  ENT_DOC_CONFIG,
  ERROR_MSGS,
  ENTERPRISE_SUCCESS_MESSAGES
} from "../../../constants/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  companyNameRules,
  nameRules,
  emailRules,
  phoneNumberRules,
  panRules,
  capitalizeWords,
  gstinRules,
  websiteRules,
  CommentsRules,
  descriptionRules,
  financialSummaryRules,
} from "../../../helpers/helpers";
import {
  addNewEnterprise,
  getDesignations,
  getEnterpriseView,
  updateErrorStatus,
  getAllRating,
} from "../../../redux/slices/enterpriseSlice";
import { getDropdownData } from "../../../redux/slices/EnqMngtSlice";
import CustomDatePicker from "../../../components/CustomDatePicker/CustomDatePicker";
import { count } from "../../../redux/slices/dealsSlice";
import { count as allCount } from "../../../redux/slices/AuthSlice";
import EntDocUploadCard from "../../../components/DocUploadCard/EntDocUploadCard";
import dayjs from "dayjs";

const NewEntForm = ({ onClose, selectedStatus, initialStep = 0 }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const { designationsData, ratingData, error, newEntSubmitLoading } =
    useSelector((state) => state.enterprise);

  const { dropdownData } = useSelector((state) => state.enqMngt);

  const [mouChecked, setMouChecked] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({
    entLogo: null,
    mou: null,
  });

  const isSubmitDisabled =
    !uploadedFiles?.entLogo || (mouChecked === true && !uploadedFiles?.mou);

  useEffect(() => {
    dispatch(getDesignations());
    dispatch(getDropdownData());
    dispatch(getAllRating());

    return () => {
      dispatch(updateErrorStatus());
    };
  }, [dispatch]);

  const handleSubmit = async (values) => {
    const formData = new FormData();

    formData.append(
      "enterprise",
      JSON.stringify({
        entName: values.entName,
        email: values.pocEmail,
        entWebsite: values.entWebsite?.trim()
          ? `https://${values.entWebsite
              .trim()
              .replace(/^(https?:\/\/)+/gi, "")}`
          : undefined,
        industryTypeId: values.industryType,
        ratingId: values.rating,
        creditExposure: Number(values.creditExposure?.replace(/,/g, "")),
        pocName: values.pocName,
        phoneNumber: values.pocPhoneNumber,
        pan: values.pan,
        gstin: values.gstin,
        incorporationDate: values.incorporationDate,
        description: values.entDesc,
        financialSummary: values.finSummary,
        comments: values.comments,
        isMouSigned: mouChecked,
      }),
    );

    formData.append("designationId", values.pocDesignation);

    Object.entries(uploadedFiles).forEach(([key, file]) => {
      if (file) {
        formData.append(key, file);
      }
    });

    const result = await dispatch(addNewEnterprise(formData));

    if (result?.payload?.statusCode === 201) {
      message.success(
        ENTERPRISE_SUCCESS_MESSAGES.create(values.entName)
      );
      dispatch(getEnterpriseView(selectedStatus));
      dispatch(count("ENTERPRISE"));
      dispatch(allCount("ALL"));
      onClose();
    }
  };

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        initialValues={{ countryCode: "+91" }}
      >
        <Row gutter={[24, 0]}>
          <Col xs={24} md={12}>
            <CustomInput
              label="Enterprise Name"
              name="entName"
              type="text"
              isRequired={true}
              placeholder="Enter your Enterprise Name"
              rules={companyNameRules("Enterprise")}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="POC Name"
              name="pocName"
              type="text"
              isRequired={true}
              placeholder="Enter POC Name"
              rules={nameRules("POC")}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="POC Email"
              name="pocEmail"
              type="email"
              isRequired={true}
              placeholder="Enter POC email"
              rules={emailRules("POC")}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="POC Phone Number"
              name="pocPhoneNumber"
              isRequired={true}
              addonBefore={<CountryCode />}
              placeholder="Phone Number"
              rules={phoneNumberRules("POC")}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomSelect
              label="POC Designation"
              name="pocDesignation"
              showSearch
              isRequired={true}
              placeholder="Select Designation"
              options={designationsData?.map((desig) => ({
                value: desig.designationId,
                label: capitalizeWords(desig.designationName),
              }))}
              rules={[
                { required: true, message: ERROR_MSGS.DESIGNATION_ERR_MEG },
              ]}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomSelect
              label="Industry Type"
              name="industryType"
              showSearch
              isRequired={true}
              placeholder="Select Industry Type"
              options={dropdownData?.industryTypeList?.map((industry) => ({
                value: industry.industryTypeId,
                label: capitalizeWords(industry.industryTypeName),
              }))}
              rules={[{ required: true, message: ERROR_MSGS.SEL_IND_TYPE }]}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="PAN"
              name="pan"
              type="text"
              isRequired={true}
              placeholder="Enter PAN"
              rules={panRules}
              isUpperCase={true}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="GSTIN"
              name="gstin"
              type="text"
              isRequired={true}
              placeholder="Enter GSTIN"
              rules={gstinRules}
              isUpperCase={true}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomDatePicker
              label="Incorporation Date"
              name="incorporationDate"
              isRequired={true}
              placeholder="Select Incorporation Date"
              rules={[{ required: true, message: ERROR_MSGS.ENTER_INC_DATE }]}
              disabledDate={(current) => current && current >= dayjs().startOf("day")}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="Enterprise Website"
              name="entWebsite"
              type="text"
              placeholder="Enter Enterprise Website"
              rules={websiteRules}
              removeHttps={true}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomSelect
              label="Rating"
              name="rating"
              placeholder="Select Rating"
              isRequired={true}
              options={ratingData?.response?.map((rating) => ({
                value: rating.ratingId,
                label: capitalizeWords(rating.ratingDesc),
              }))}
              rules={[{ required: true, message: ERROR_MSGS.SEL_RATE_ERR_MSG }]}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="Enterprise Exposure Amount"
              name="creditExposure"
              isRequired={true}
              allowComma={true}
              placeholder="Enter Enterprise Exposure Amount"
              rules={[
                {
                  required: true,
                  message: ERROR_MSGS.EXPOSURE_AMT_ERR_MSG,
                },
                {
                  validator: (_, value) => {
                    // Let required rule handle empty values
                    if (!value) {
                      return Promise.resolve();
                    }

                    const numericValue = value?.replace(/,/g, "");

                    if (Number(numericValue) <= 0) {
                      return Promise.reject(
                        new Error("Exposure amount must be greater than 0"),
                      );
                    }

                    if (String(numericValue).length > 15) {
                      return Promise.reject(
                        new Error("Exposure amount cannot exceed 15 digits"),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Col>
          <Col xs={24}>
            <CustomInput
              label="Enterprise Description (Max 500 chars)"
              name="entDesc"
              rows={3}
              isTextArea={true}
              isRequired={true}
              type="text"
              placeholder="Enter your Enterprise Description"
              rules={descriptionRules("Enterprise")}
            />
          </Col>
          <Col xs={24}>
            <CustomInput
              label="Financial Summary (Max 500 chars)"
              name="finSummary"
              rows={3}
              isRequired={true}
              isTextArea={true}
              type="text"
              placeholder="Enter your Financial Summary"
              rules={financialSummaryRules}
            />
          </Col>
          <Col xs={24}>
            <CustomInput
              label="Comments (Max 500 chars)"
              name="comments"
              rows={3}
              isRequired={true}
              isTextArea={true}
              type="text"
              placeholder="Enter your Comments"
              rules={CommentsRules}
            />
          </Col>
          {ENT_DOC_CONFIG.map((doc) => (
            <Col xs={24} lg={12} key={doc.key} style={{ marginBottom: 20 }}>
              <EntDocUploadCard
                doc={doc}
                uploadedFile={uploadedFiles[doc.key]}
                onFileChange={(file) =>
                  setUploadedFiles((prev) => ({
                    ...prev,
                    [doc.key]: file,
                  }))
                }
                {...(doc.key === "mou" ? { mouChecked, setMouChecked } : {})}
              />
            </Col>
          ))}
        </Row>

        {/* FOOTER BUTTONS */}
        <FormFooter
          onCancelClick={() => {
            onClose();
            form.resetFields();
          }}
          error={error}
          disabled={isSubmitDisabled}
          submitLoading={newEntSubmitLoading}
          btnName={BTN_TXT.SUBMIT}
        />
      </Form>
    </>
  );
};

export default NewEntForm;
